import { useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';

/**
 * Custom hook to protect against DEFAULT tenant usage and auto-refresh tenant context
 */
export const useTenantProtection = () => {
  const { getCurrentTenant, user, logout, ensureTenantContext } = useAuth();

  // Function to get tenant code with protection
  const getProtectedTenantCode = useCallback(() => {
    const currentTenant = getCurrentTenant();
    const tenantCode = currentTenant?.code || currentTenant?.tenant_code;
    
    console.log('🔍 TENANT_PROTECTION: getProtectedTenantCode called', {
      currentTenant,
      tenantCode,
      userId: user?.userId,
      userTenants: user?.tenants?.length || 0
    });
    
    // If no tenant code or it's DEFAULT, attempt self-heal with ensureTenantContext
    if (!tenantCode || tenantCode === 'DEFAULT') {
      console.warn('🚨 Tenant context lost! Attempting auto-restore...', { userId: user?.userId, currentTenant });
      ensureTenantContext();
      return null;
    }
    
    return tenantCode;
  }, [getCurrentTenant, user, logout, ensureTenantContext]);

  // Function to get tenant collection name safely
  const getProtectedCollectionName = useCallback((baseCollection = 'CRM') => {
    const tenantCode = getProtectedTenantCode();
    
    if (!tenantCode) {
      return `INVALID_${baseCollection}`; // This will cause API errors, preventing wrong data access
    }
    
    return `${tenantCode}_${baseCollection}`;
  }, [getProtectedTenantCode]);

  // Function to get tenant info safely
  const getProtectedTenantInfo = useCallback(() => {
    const currentTenant = getCurrentTenant();
    const tenantCode = getProtectedTenantCode();
    
    if (!tenantCode) {
      return {
        code: 'INVALID',
        name: 'Invalid Tenant - Please Login Again'
      };
    }
    
    return {
      code: tenantCode,
      name: currentTenant?.name || currentTenant?.tenant_name || tenantCode
    };
  }, [getCurrentTenant, getProtectedTenantCode]);

  // Monitor tenant context on component mount and user changes
  useEffect(() => {
    if (user) {
      const tenantCode = getProtectedTenantCode();
      if (!tenantCode) {
        console.warn('🚨 Invalid tenant context detected. Kicking off auto-restore.');
        ensureTenantContext();
      } else {
        console.log('✅ Valid tenant context:', tenantCode, 'for user:', user.userId);
      }
    }
  }, [user, getProtectedTenantCode]);

  return {
    getProtectedTenantCode,
    getProtectedCollectionName,
    getProtectedTenantInfo
  };
};

export default useTenantProtection;